import React from 'react';
import { useHref, useLinkClickHandler } from 'react-router-dom';
import type { LinkProps as ReactLinkProps, To } from 'react-router-dom';

export interface LinkProps extends Omit<ReactLinkProps, 'to'> {
  to: To;
  component?: any;
  innerRef?: any; // TODO: deprecate
  href?: string;
}

/**
 * The public API for rendering a history-aware <a>.
 *
 * Migrated from a hand-rolled `__RouterContext` consumer (react-router v5) to
 * the public React Router v6 hooks. `useLinkClickHandler` reproduces the
 * original left-click / modifier-key / target handling, and an explicit `href`
 * (the absolute SEO url built by the SeoLink wrappers) still wins over the
 * router-derived href for the rendered anchor.
 */
export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  ({ component: Component = 'a', replace, state, to, innerRef, href, onClick, target, ...rest }, forwardedRef) => {
    const routerHref = useHref(to);
    const handleLinkClick = useLinkClickHandler<HTMLAnchorElement>(to, { replace, target, state });

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
      try {
        onClick?.(event);
      } catch (ex) {
        event.preventDefault();
        throw ex;
      }

      if (!event.defaultPrevented) {
        handleLinkClick(event);
      }
    };

    return (
      /* eslint-disable-next-line jsx-a11y/anchor-has-content */
      <Component
        {...rest}
        href={href ?? routerHref}
        onClick={handleClick}
        target={target}
        ref={forwardedRef ?? innerRef}
      />
    );
  },
);
